import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { RecaptchaConfig, RecaptchaCallbacks, UseCheckboxRecaptchaReturn } from './types';
import { getRecaptchaMethods, injectScript } from './utils';

const RECAPTCHA_SCRIPT_PATH = 'https://www.google.com/recaptcha/enterprise.js?render=explicit';
const RECAPTCHA_SCRIPT_REGEX = /(http|https):\/\/(www)?.+\/recaptcha/;

const useCheckboxRecaptcha = (
	config: RecaptchaConfig,
	callbacks: RecaptchaCallbacks,
): UseCheckboxRecaptchaReturn => {
	const [instanceId, updateInstanceId] = useState<unknown>(null);
	const [token, updateToken] = useState<unknown>(null);

	const callbacksRef = useRef(callbacks);
	const configRef = useRef(config);
	const onVerifyWrapper = useCallback((newToken: string) => {
		updateToken(newToken);
		if (callbacksRef.current.onVerify) {
			callbacksRef.current.onVerify(newToken);
		}
	}, []);

	const callBackRef = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
		async (node) => {
			if (!node || __SERVER__) {
				return;
			}
			try {
				const sriptLoaded = await injectScript(`${RECAPTCHA_SCRIPT_PATH}`, RECAPTCHA_SCRIPT_REGEX);
				if (sriptLoaded && callbacksRef.current.onLoad) {
					callbacksRef.current.onLoad();
				}
				const newInstanceId = getRecaptchaMethods().render(node, {
					callback: onVerifyWrapper,
					'expired-callback': callbacksRef.current.onExpire,
					'error-callback': callbacksRef.current.onError,
					...configRef.current,
				});
				updateInstanceId(newInstanceId);
				if (callbacksRef.current.onRender) {
					callbacksRef.current.onRender();
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (callbacksRef.current.onScriptError) {
					callbacksRef.current.onScriptError(error instanceof Error ? error : undefined);
				}
			}
		},
		[onVerifyWrapper],
	);

	const recaptchaComponent = useMemo(() => <div ref={callBackRef} />, [callBackRef]);
	const reset = useCallback(() => {
		if (instanceId !== null) {
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number | undefined'.
			getRecaptchaMethods().reset(instanceId);
			updateToken(null);
		}
	}, [instanceId]);
	const getResponse = useCallback(() => {
		if (instanceId !== null) {
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number | undefined'.
			return getRecaptchaMethods().getResponse(instanceId);
		}
		return '';
	}, [instanceId]);
	return {
		recaptchaComponent,
		reset,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string | null'.
		token,
		getResponse,
	};
};
export default useCheckboxRecaptcha;
